import type { ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';

export const TURN_STYLES = {
  /* ----------------------------- Accents ----------------------------- */
  primaryAccentColor: '#7364DB',
  primaryAccentColorHover: '#a99ef03c',

  secondaryAccentColor: '#1976d2',
  secondaryAccentColorHover: '#1976d20a',

  darkSecondaryAccentColor: '#90caf9',

  /* ----------------------------- Background ----------------------------- */
  backgroundColor: '#f8fafc',
  darkBackgroundColor: '#151825',

  surfaceColor: '#fff',
  darkSurfaceColor: '#26273B',

  /* -------------------- Border Colors --------------------- */
  borderColor: '#e5e7eb',
  darkBorderColor: '#3a3c5c',

  /* -------------------- Button Colors --------------------- */
  secondaryButtonColor: '#9A9AAF',
  darkSecondaryButtonColor: '#64646F',

  /* -------------------- Chips Dark Colors --------------------- */
  darkErrorDark: '#7f1d1d',
  darkErrorLight: '#fecaca',

  darkInfoDark: '#3b82f6',
  darkInfoLight: '#bfdbfe',

  darkNeutralDark: '#111827',
  darkNeutralLight: '#e5e7eb',

  darkOrangeDark: '#f97316',
  darkOrangeLight: '#fed7aa',

  darkSuccessDark: '#16a34a',
  darkSuccessLight: '#bbf7d0',

  darkVioletDark: '#5b4fd6',
  darkVioletLight: '#ddd6fe',

  darkWarningDark: '#d97706',
  darkWarningLight: '#fde68a',

  /* -------------------- Chips Light Colors --------------------- */
  errorDark: '#991b1b',
  errorLight: '#fee2e2',

  infoDark: '#60a5fa',
  infoLight: '#dbeafe',

  neutralDark: '#1f2937',
  neutralLight: '#f3f4f6',

  orangeDark: '#fb923c',
  orangeLight: '#ffedd5',

  successDark: '#22c55e',
  successLight: '#dcfce7',

  violetDark: '#7364DB',
  violetLight: '#ede9fe',

  warningDark: '#efb100',
  warningLight: '#fef9c3',

  /* ----------------------------- Text ----------------------------- */
  primaryColor: '#6b7280',
  primaryColorHover: '#a99ef03c',
  darkPrimaryColor: 'white',

  secondaryColor: '#1976d2',
  secondaryColorHover: '#1976d20a',
  darkSecondaryColor: '#d6d9f2',

  /* ----------------------------- Table ----------------------------- */
  tableHeaderColor: '#EFEFF4',
  darkTableHeaderColor: '#3b3d5c',
} as const;

/* -------------------------------------------------------------------------------------------------
 * BASE THEME (shared between light/dark)
 * - This prevents “resetting to MUI defaults” when switching mode
 * ------------------------------------------------------------------------------------------------- */
export const BASE_THEME: ThemeOptions = {
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    body1: {
      fontSize: '14px',
    },
    body2: {
      fontSize: '13px',
    },
    h5: {
      fontSize: '20px',
    },
    h6: {
      fontSize: '17px',
    },
  },

  palette: {
    success: {
      main: '#22C55E', // primary success color
      light: '#86EFAC',
      dark: '#15803D',
      contrastText: '#fff',
    },
    turn_info: {
      main: TURN_STYLES.infoDark,
      dark: TURN_STYLES.infoDark,
      light: TURN_STYLES.infoLight,
    },
    turn_success: {
      main: TURN_STYLES.successDark,
      dark: TURN_STYLES.successDark,
      light: TURN_STYLES.successLight,
    },
    turn_error: {
      main: TURN_STYLES.errorDark,
      dark: TURN_STYLES.errorDark,
      light: TURN_STYLES.errorLight,
    },
    turn_violet: {
      main: TURN_STYLES.violetDark,
      dark: TURN_STYLES.violetDark,
      light: TURN_STYLES.violetLight,
    },
    turn_orange: {
      main: TURN_STYLES.orangeDark,
      dark: TURN_STYLES.orangeDark,
      light: TURN_STYLES.orangeLight,
    },
    turn_neutral: {
      main: TURN_STYLES.neutralDark,
      dark: TURN_STYLES.neutralDark,
      light: TURN_STYLES.neutralLight,
    },
    turn_warning: {
      main: TURN_STYLES.warningDark,
      dark: TURN_STYLES.warningDark,
      light: TURN_STYLES.warningLight,
    },
  },

  components: {
    /* ----------------------------- Modals (shared) ----------------------------- */
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: '70px',
          backgroundImage: theme.palette.mode === 'dark' && 'none',
          backgroundColor: theme.palette.mode === 'dark' ? TURN_STYLES.darkSurfaceColor : 'white',
          boxShadow: 'none',

          '& .RaAppBar-toolbar': {
            height: '100%',
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? TURN_STYLES.darkBorderColor : TURN_STYLES.borderColor}`,
          },

          '& .RaAppBar-title': {
            color: theme.palette.mode === 'dark' ? TURN_STYLES.darkSecondaryColor : TURN_STYLES.primaryColor,
            fontWeight: 600,
          },

          '& .MuiIconButton-root': {
            color: theme.palette.mode === 'dark' ? TURN_STYLES.darkSecondaryColor : TURN_STYLES.primaryColor,
          },

          '& .MuiIconButton-root:hover': {
            backgroundColor: TURN_STYLES.primaryColorHover,
          },
        }),
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        popper: ({ theme }) => ({
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${theme.palette.mode === 'dark' ? TURN_STYLES.darkBorderColor : TURN_STYLES.borderColor}`,
          },
        }),

        option: {
          minHeight: '42px !important',
          borderRadius: '8px',
          margin: '4px',
          transition: 'background-color 0.2s ease',

          // Hover
          '&:hover': {
            backgroundColor: `${TURN_STYLES.primaryAccentColor} !important`,
            color: '#fff',
          },

          // Keyboard focus
          '&.Mui-focusVisible': {
            backgroundColor: `${TURN_STYLES.primaryAccentColor} !important`,
            color: '#fff',
          },

          // Selected
          '&.Mui-selected': {
            backgroundColor: `${TURN_STYLES.primaryAccentColor} !important`,
            color: '#fff',
          },

          // Selected + hover (important)
          '&.Mui-selected:hover': {
            backgroundColor: `${TURN_STYLES.primaryAccentColor} !important`,
            color: '#fff',
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.mode === 'dark' ? TURN_STYLES.darkSurfaceColor : TURN_STYLES.surfaceColor,
          borderRadius: '8px',
          boxShadow: 'none',

          '&:not(:has(.turn-main-list))': {
            border: theme.palette.mode === 'dark' ? 'none' : `1px solid ${TURN_STYLES.borderColor}`,
          },
        }),
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: '0',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          minWidth: '350px',
          maxWidth: '420px',
          backgroundColor: theme.palette.mode === 'dark' ? TURN_STYLES.darkSurfaceColor : 'white',
          borderRadius: '12px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
        }),
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          '& .MuiButton-root': {
            width: '100px',
            backgroundColor: TURN_STYLES.primaryAccentColor,
            color: 'white',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: TURN_STYLES.violetDark,
            },
          },
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingTop: '8px',
          paddingBottom: '16px',
        },
      },
    },

    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '14px',
          color: theme.palette.mode === 'dark' ? TURN_STYLES.darkSecondaryColor : TURN_STYLES.neutralDark,
        }),
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '1.125rem',
          fontWeight: 600,
          color: theme.palette.mode === 'dark' ? TURN_STYLES.darkSecondaryColor : TURN_STYLES.neutralDark,
          paddingBottom: '8px',
        }),
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            marginTop: '0',
            marginBottom: '6px',
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.mode === 'dark' && TURN_STYLES.darkSecondaryColor,
          backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : 'white',
          paddingRight: '6px',
          textTransform: 'capitalize',
          lineHeight: '24px',

          '&:not(.MuiInputLabel-shrink)': {
            top: '-10%',
            fontSize: '14px',
          },

          '&.Mui-disabled': {
            color: TURN_STYLES.secondaryButtonColor,
            backgroundColor: 'transparent',
          },

          '&.Mui-focused': {
            color: TURN_STYLES.primaryAccentColor,
          },
        }),
      },
    },

    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          color:
            theme.palette.mode === 'dark' ? TURN_STYLES.darkSecondaryAccentColor : TURN_STYLES.secondaryAccentColor,
          textDecoration: 'none',
        }),
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          border: `1px solid ${theme.palette.mode === 'dark' ? TURN_STYLES.darkBorderColor : TURN_STYLES.borderColor}`,
          padding: '0 4px',
        }),
        list: {
          padding: '0',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          height: '42px',
          margin: '4px 0',
          borderRadius: '8px',

          '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
            color: 'white',
            backgroundColor: TURN_STYLES.primaryAccentColor,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: '256px',
          maxHeight: '42px',
          height: '42px',
          fontSize: '14px',
          borderRadius: '8px',

          color: theme.palette.mode === 'dark' && TURN_STYLES.darkSecondaryColor,
          backgroundColor: theme.palette.mode === 'dark' && TURN_STYLES.darkSurfaceColor,

          '&.MuiInputBase-multiline': {
            width: '350px',
            maxHeight: '140px',
            height: '140px',
            overflow: 'auto',
          },

          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' ? TURN_STYLES.darkBorderColor : TURN_STYLES.borderColor,
          },

          '&:placeholder': {
            fontSize: '14px',
          },

          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: TURN_STYLES.primaryAccentColor,
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${TURN_STYLES.primaryAccentColor}`,
          },

          '&.Mui-disabled': {
            cursor: 'not-allowed',

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.mode === 'dark' ? TURN_STYLES.darkBorderColor : TURN_STYLES.borderColor,
              backgroundColor: theme.palette.mode === 'dark' ? '#2B2C42' : 'rgba(229, 231, 235, 0.5)',
              opacity: theme.palette.mode === 'dark' && 0.5,
            },

            '& .MuiInputBase-input': {
              cursor: 'not-allowed',
            },
          },

          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0px 1000px transparent inset', // background color
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0px 1000px transparent inset',
          },
        }),

        input: {
          '&.Mui-disabled': {
            cursor: 'not-allowed',
          },
        },

        notchedOutline: ({ theme }) => ({
          borderColor: theme.palette.mode === 'dark' ? TURN_STYLES.darkBorderColor : TURN_STYLES.borderColor,
        }),
      },
    },

    MuiStack: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .RaFileInput-dropZone': {
            width: '256px',
            minHeight: '96px',
            border: `1px solid ${theme.palette.mode === 'dark' ? TURN_STYLES.darkBorderColor : TURN_STYLES.borderColor}`,
            borderRadius: '4px',
            backgroundColor: theme.palette.mode === 'dark' ? `${TURN_STYLES.darkSurfaceColor} !important` : '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',

            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'none' : '#f1f5f9',
              borderColor: TURN_STYLES.primaryAccentColor,
            },

            '& p': {
              color: TURN_STYLES.secondaryButtonColor,
              margin: 0,
            },

            '& + .MuiFormHelperText-root': {
              display: 'none',
            },
          },

          '& .RaFileInput-removeButton': {
            display: 'none',
          },

          '& .RaFileInputPreview-removeButton': {
            display: 'none',
          },
        }),
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: TURN_STYLES.primaryAccentColor,
            '& .MuiSwitch-thumb': {
              color: TURN_STYLES.primaryAccentColor,
            },
          },
          '& .MuiSwitch-switchBase.Mui-checked ~ .MuiSwitch-track, & .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
            {
              backgroundColor: TURN_STYLES.primaryAccentColor,
              opacity: 0.5,
            },
        },
        track: {
          backgroundColor: 'rgba(0, 0, 0, 0.38)',
          opacity: 1,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.mode === 'dark' ? TURN_STYLES.darkSecondaryColor : TURN_STYLES.primaryColor,

          '&.Mui-selected': {
            color: TURN_STYLES.primaryAccentColor,
          },
          '&:hover': {
            color: TURN_STYLES.primaryAccentColor,
          },
        }),
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: theme.palette.mode === 'dark' && `1px solid ${TURN_STYLES.darkBorderColor}`,
        }),
        indicator: {
          backgroundColor: TURN_STYLES.primaryAccentColor,
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(125, 173, 255, 0.1) !important',
          },
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        root: ({ theme }) => ({
          // fontSize: '14px',

          '&.RaLabeled-label': {
            color: theme.palette.mode === 'dark' && 'white',
            fontWeight: 700,
          },
        }),
      },
    },
  },
};

/**
 * LIGHT theme options.
 * - Spread BASE_THEME so shared stuff always persists
 */
export const LIGHT_THEME_OPTIONS: ThemeOptions = {
  ...BASE_THEME,

  palette: {
    ...(BASE_THEME.palette as any),
    mode: 'light',
    background: {
      default: TURN_STYLES.backgroundColor,
      paper: TURN_STYLES.surfaceColor,
    },
    divider: TURN_STYLES.borderColor,
    text: {
      primary: TURN_STYLES.primaryColor,
    },
  },

  components: {
    ...BASE_THEME.components,
  },
};

/**
 * DARK THEME OPTIONS
 * - Spread BASE_THEME so shared stuff always persists
 */
export const DARK_THEME_OPTIONS: ThemeOptions = {
  ...BASE_THEME,

  palette: {
    ...(LIGHT_THEME_OPTIONS.palette as any),
    mode: 'dark',
    background: {
      default: TURN_STYLES.darkBackgroundColor,
      paper: TURN_STYLES.darkSurfaceColor,
    },
    divider: TURN_STYLES.darkBorderColor,
    text: {
      primary: TURN_STYLES.darkSecondaryColor,
    },
  },

  components: {
    ...BASE_THEME.components,

    RaNotification: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            backgroundColor: TURN_STYLES.darkSurfaceColor,
            color: TURN_STYLES.darkSecondaryColor,
            border: `1px solid ${TURN_STYLES.darkBorderColor}`,
          },

          '& .MuiSnackbarContent-message': {
            color: TURN_STYLES.darkSecondaryColor,
            fontWeight: 500,
          },
        },
      },
    },
  },
};

/**
 * Export actual themes to pass into <Admin theme={...} darkTheme={...} />
 */
export const lightTheme = createTheme(LIGHT_THEME_OPTIONS);
export const darkTheme = createTheme(DARK_THEME_OPTIONS);
